import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';

export interface LegendItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface LegendSection {
  title: string;
  items: LegendItem[];
}

interface IconLegendModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: LegendSection[];
  title?: string;
  children?: React.ReactNode;
}

const IconLegendModal: React.FC<IconLegendModalProps> = ({ isOpen, onClose, sections, title = 'Icon Legend', children }) => {
  // Read-only legend: Alt+C closes, and the layer keeps Alt+S off the grid below.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />

        {/* Content */}
        <div className="p-6">
          {children ?? (sections.length === 0 ? (
            // Coming Soon message when no sections are configured
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                <InformationCircleIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-heading mb-2">
                Coming Soon
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Icon legend for this page is currently being developed.
              </p>
            </div>
          ) : (
            // Show configured legend sections
            <div className="space-y-6">
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  <h3 className="text-lg font-semibold text-heading mb-4">
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                      >
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <p className="font-medium text-heading">
                            {item.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IconLegendModal;
